import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  Link,
  useColorModeValue,
  List,
  ListItem,
  ListIcon,
} from '@chakra-ui/react';
import { FaCheckCircle } from 'react-icons/fa';

const HipaaBaa = () => {
  const bgColor = useColorModeValue('white', 'gray.800');

  return (
    <Box>
      {/* Hero Section */}
      <Box
        bg="linear-gradient(120deg, #14B7D6 0%, #1E9BE8 46%, #2E7CF0 100%)"
        color="white"
        pt={{ base: '130px', lg: '190px' }}
        pb={{ base: 12, lg: 16 }}
        position="relative"
        overflow="hidden"
      >
        <Container maxW="container.xl">
          <VStack spacing={4} textAlign="center">
            <Heading size="xl" fontWeight="800">
              HIPAA Business Associate Addendum
            </Heading>
            <Text fontSize="lg" opacity={0.95}>
              Google Workspace HIPAA Compliance
            </Text>
            <Text fontSize="sm" opacity={0.9}>
              Last Modified: August 25, 2021
            </Text>
          </VStack>
        </Container>
      </Box>

      {/* Content Section */}
      <Container maxW="container.lg" py={12}>
        <Box bg={bgColor} p={8} borderRadius="lg" boxShadow="md">
          <VStack align="stretch" spacing={6}>
            <Box>
              <Text fontSize="md" lineHeight="tall" color="gray.700">
                This HIPAA Business Associate Addendum ("BAA") is entered into between Google and Lake Lucien Surgery Center ("Customer"), and supplements, amends and is incorporated into the Services Agreement(s) solely with respect to Covered Services.
              </Text>
            </Box>

            <Box>
              <Heading size="md" mb={3} color="brand.700">
                Key Definitions
              </Heading>
              <VStack align="stretch" spacing={3} pl={4}>
                <Text fontSize="sm" color="gray.600">
                  <strong>Business Associate:</strong> As defined under HIPAA - an entity that performs services for a Covered Entity involving the use or disclosure of Protected Health Information.
                </Text>
                <Text fontSize="sm" color="gray.600">
                  <strong>Covered Entity:</strong> Health plans, healthcare clearinghouses, and healthcare providers who transmit health information electronically.
                </Text>
                <Text fontSize="sm" color="gray.600">
                  <strong>Protected Health Information (PHI):</strong> Individually identifiable health information transmitted or maintained in any form or medium.
                </Text>
                <Text fontSize="sm" color="gray.600">
                  <strong>HIPAA:</strong> Health Insurance Portability and Accountability Act of 1996 and its implementing regulations.
                </Text>
              </VStack>
            </Box>

            <Box>
              <Heading size="md" mb={3} color="brand.700">
                Applicability
              </Heading>
              <Text fontSize="md" lineHeight="tall" color="gray.700" mb={3}>
                This BAA applies to the extent Customer is acting as a Covered Entity or a Business Associate to create, receive, maintain, or transmit PHI via a Covered Service and Google is deemed under HIPAA to be acting as a Business Associate or Subcontractor of Customer.
              </Text>
            </Box>

            <Box>
              <Heading size="md" mb={3} color="brand.700">
                Permitted Use and Disclosure of PHI
              </Heading>
              <Text fontSize="md" lineHeight="tall" color="gray.700" mb={3}>
                Google may use and disclose PHI only:
              </Text>
              <List spacing={2} pl={4}>
                <ListItem fontSize="sm" color="gray.600">
                  <ListIcon as={FaCheckCircle} color="brand.500" />
                  As permitted or required by the Services Agreements and/or this BAA
                </ListItem>
                <ListItem fontSize="sm" color="gray.600">
                  <ListIcon as={FaCheckCircle} color="brand.500" />
                  As required by law
                </ListItem>
                <ListItem fontSize="sm" color="gray.600">
                  <ListIcon as={FaCheckCircle} color="brand.500" />
                  For proper management and administration with appropriate safeguards
                </ListItem>
              </List>
            </Box>

            <Box>
              <Heading size="md" mb={3} color="brand.700">
                Appropriate Safeguards
              </Heading>
              <Text fontSize="md" lineHeight="tall" color="gray.700">
                Google and Customer will each use appropriate safeguards designed to prevent against unauthorized use or disclosure of PHI, and as otherwise required under HIPAA, with respect to the Covered Services.
              </Text>
            </Box>

            <Box>
              <Heading size="md" mb={3} color="brand.700">
                Reporting and Related Obligations
              </Heading>
              <Text fontSize="md" lineHeight="tall" color="gray.700" mb={3}>
                Google will promptly notify Customer of:
              </Text>
              <List spacing={2} pl={4}>
                <ListItem fontSize="sm" color="gray.600">
                  <ListIcon as={FaCheckCircle} color="brand.500" />
                  Any Security Incident of which Google becomes aware
                </ListItem>
                <ListItem fontSize="sm" color="gray.600">
                  <ListIcon as={FaCheckCircle} color="brand.500" />
                  Any Breach that Google discovers (within 60 calendar days of discovery)
                </ListItem>
              </List>
              <Text fontSize="sm" color="gray.600" mt={3}>
                Notifications will include details of the breach, steps taken to mitigate risks, and recommended actions for Customer.
              </Text>
            </Box>

            <Box>
              <Heading size="md" mb={3} color="brand.700">
                Subcontractors
              </Heading>
              <Text fontSize="md" lineHeight="tall" color="gray.700">
                Google will ensure that any Subcontractors used to perform obligations requiring access to PHI are bound by written obligations that provide the same material level of protection for PHI as this BAA.
              </Text>
            </Box>

            <Box bg="blue.50" p={6} borderRadius="md" borderLeft="4px solid" borderColor="brand.500">
              <Heading size="sm" mb={3} color="brand.700">
                Covered Services
              </Heading>
              <Text fontSize="sm" color="gray.700" lineHeight="tall" mb={3}>
                This BAA covers Google Workspace services as specifically identified at:
              </Text>
              <Link 
                href="https://workspace.google.com/terms/2015/1/hipaa_functionality.html" 
                color="brand.600" 
                fontSize="sm"
                isExternal
              >
                Google Workspace HIPAA Covered Services
              </Link>
            </Box>

            <Box bg="orange.50" p={6} borderRadius="md" borderLeft="4px solid" borderColor="orange.500">
              <Heading size="sm" mb={3} color="orange.700">
                Important Notice
              </Heading>
              <Text fontSize="sm" color="gray.700" lineHeight="tall">
                This is a summary of key sections. For the complete HIPAA Business Associate Addendum, implementation guides, and configuration requirements, please visit{' '}
                <Link href="https://cloud.google.com/security/compliance/hipaa-compliance" color="brand.600" isExternal>
                  Google Cloud HIPAA Compliance
                </Link>
                .
              </Text>
            </Box>

            <Box pt={6} borderTop="1px solid" borderColor="gray.200">
              <VStack align="stretch" spacing={3}>
                <Text fontSize="sm" color="gray.500">
                  Lake Lucien Surgery Center maintains HIPAA compliance through:
                </Text>
                <List spacing={2} pl={4}>
                  <ListItem fontSize="sm" color="gray.500">
                    • Signed Business Associate Agreements with all vendors handling PHI
                  </ListItem>
                  <ListItem fontSize="sm" color="gray.500">
                    • Implementation of appropriate technical, physical, and administrative safeguards
                  </ListItem>
                  <ListItem fontSize="sm" color="gray.500">
                    • Regular security risk assessments and staff training
                  </ListItem>
                  <ListItem fontSize="sm" color="gray.500">
                    • Documented policies and procedures for PHI handling
                  </ListItem>
                </List>
              </VStack>
            </Box>
          </VStack>
        </Box>
      </Container>
    </Box>
  );
};

export default HipaaBaa;
